import React, { useCallback, useEffect, useState } from 'react';

import BrushIcon from '@mui/icons-material/Brush';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GridViewIcon from '@mui/icons-material/GridView';
import MenuIcon from '@mui/icons-material/Menu';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
   Accordion,
   AccordionDetails,
   AccordionSummary,
   Box,
   Button,
   IconButton,
   MenuItem,
   Select,
   Slider,
   Tooltip,
   Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSignalRState } from 'contexts/SignalRContext';
import {
   setCoatThickness,
   setCoatType,
   setPreviewZoom,
   setSelectedLayer,
   stateOfGeneralSlice,
} from 'state/general/general.slice';
import { CoatThickness, CoatType } from 'state/general/general.types';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const PANEL_WIDTH = 256;
const PRIMARY_COLOR = '#c3a05f';

const COLLAPSED_WIDTH = 48;

const PanelAside = styled(Box)(({ theme }) => ({
   width: PANEL_WIDTH,
   minWidth: PANEL_WIDTH,
   backgroundColor: theme.palette.mode === 'light' ? '#f8f8f8' : '#2d2d2d',
   borderRight: `1px solid ${theme.palette.divider}`,
   display: 'flex',
   flexDirection: 'column',
   flexShrink: 0,
   userSelect: 'none',
   minHeight: 0,
   overflowY: 'auto',
   transition: 'width 0.25s ease, min-width 0.25s ease',
}));

const PanelHeader = styled(Box)(({ theme }) => ({
   padding: theme.spacing(1.5, 2),
   backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#383838',
   borderBottom: `1px solid ${theme.palette.divider}`,
   display: 'flex',
   alignItems: 'center',
   gap: theme.spacing(1),
}));

const varnishPaletteOptions: { value: CoatType; label: string; gradient: string }[] = [
   {
      value: 'varnish',
      label: 'Sense',
      gradient:
         'linear-gradient(135deg, hsl(0,0%,94%) 0%, hsl(0,0%,98%) 25%, hsl(0,0%,88%) 50%, hsl(0,0%,96%) 75%, hsl(0,0%,92%) 100%)',
   },
];

const coatOptions: { value: CoatType; label: string; gradient: string }[] = [
   {
      value: 'silver',
      label: 'Silver',
      gradient:
         'linear-gradient(135deg, hsl(0,0%,75%) 0%, hsl(0,0%,90%) 25%, hsl(0,0%,55%) 50%, hsl(0,0%,85%) 75%, hsl(0,0%,65%) 100%)',
   },
   {
      value: 'silverBlue',
      label: 'Silver Blue',
      gradient:
         'linear-gradient(135deg, hsl(218,40%,58%) 0%, hsl(218,45%,70%) 25%, hsl(218,35%,42%) 50%, hsl(218,42%,62%) 75%, hsl(218,38%,52%) 100%)',
   },
   {
      value: 'bronze',
      label: 'Bronze',
      gradient:
         'linear-gradient(135deg, hsl(30,45%,38%) 0%, hsl(35,55%,52%) 25%, hsl(28,50%,30%) 50%, hsl(32,50%,48%) 75%, hsl(30,40%,35%) 100%)',
   },
   {
      value: 'gold',
      label: 'Gold',
      gradient:
         'linear-gradient(135deg, hsl(43,80%,45%) 0%, hsl(48,95%,65%) 25%, hsl(40,90%,35%) 50%, hsl(45,100%,55%) 75%, hsl(43,85%,42%) 100%)',
   },
   {
      value: 'roseGold',
      label: 'Rose Gold',
      gradient:
         'linear-gradient(135deg, hsl(10,50%,60%) 0%, hsl(15,65%,75%) 25%, hsl(5,55%,50%) 50%, hsl(12,60%,70%) 75%, hsl(8,50%,55%) 100%)',
   },
   {
      value: 'champagne',
      label: 'Champagne',
      gradient:
         'linear-gradient(135deg, hsl(40,55%,88%) 0%, hsl(40,45%,95%) 25%, hsl(40,60%,78%) 50%, hsl(40,50%,92%) 75%, hsl(40,55%,85%) 100%)',
   },
   {
      value: 'emeraldGreen',
      label: 'Emerald Green',
      gradient:
         'linear-gradient(135deg, hsl(145,45%,42%) 0%, hsl(145,40%,55%) 25%, hsl(145,50%,30%) 50%, hsl(145,45%,50%) 75%, hsl(145,48%,38%) 100%)',
   },
   {
      value: 'burgundy',
      label: 'Burgundy',
      gradient:
         'linear-gradient(135deg, hsl(350,60%,26%) 0%, hsl(350,50%,35%) 25%, hsl(350,65%,16%) 50%, hsl(350,55%,30%) 75%, hsl(350,58%,22%) 100%)',
   },
   {
      value: 'lavender',
      label: 'Lavender',
      gradient:
         'linear-gradient(135deg, hsl(270,35%,72%) 0%, hsl(270,30%,78%) 25%, hsl(270,40%,62%) 50%, hsl(270,35%,70%) 75%, hsl(270,38%,68%) 100%)',
   },
   {
      value: 'royalPurple',
      label: 'Royal Purple',
      gradient:
         'linear-gradient(135deg, hsl(270,35%,46%) 0%, hsl(270,30%,56%) 25%, hsl(270,40%,33%) 50%, hsl(270,35%,52%) 75%, hsl(270,38%,42%) 100%)',
   },
   {
      value: 'black',
      label: 'Black',
      gradient:
         'linear-gradient(135deg, hsl(0,0%,18%) 0%, hsl(0,0%,28%) 25%, hsl(0,0%,10%) 50%, hsl(0,0%,22%) 75%, hsl(0,0%,15%) 100%)',
   },
];

const thicknessOptions: { value: CoatThickness; label: string; height: string }[] = [
   { value: 'min', label: 'Min', height: '3px' },
   { value: 'mid', label: 'Mid', height: '8px' },
   { value: 'max', label: 'Max', height: '12px' },
];

const ZOOM_LEVELS = [25, 50, 75, 100, 150, 200, 300, 400];
const ZOOM_MIN = ZOOM_LEVELS[0];
const ZOOM_MAX = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

const StyledAccordion = styled(Accordion)({
   '&:before': { display: 'none' },
   boxShadow: 'none',
   '&.Mui-expanded': { margin: 0 },
   '& .MuiAccordionSummary-root': { minHeight: 36 },
   '& .MuiAccordionSummary-content': { margin: 0 },
   '& .MuiAccordionDetails-root': { padding: '0 12px 8px' },
});

const MetallicSwatch = styled('button', {
   shouldForwardProp: (prop) => prop !== 'gradient' && prop !== 'isSelected',
})<{ gradient: string; isSelected: boolean }>(({ theme, gradient, isSelected }) => ({
   padding: 0,
   margin: 0,
   font: 'inherit',
   width: '100%',
   aspectRatio: '1',
   borderRadius: theme.shape.borderRadius,
   border: `2px solid ${isSelected ? '#c3a05f' : theme.palette.divider}`,
   cursor: 'pointer',
   background: gradient,
   boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.12)' : '0 1px 2px rgba(0,0,0,0.08)',
   transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
   transform: isSelected ? 'scale(1.05)' : 'scale(1)',
   '&:hover': {
      borderColor: isSelected ? '#c3a05f' : 'rgba(195, 160, 95, 0.5)',
      transform: 'scale(1.05)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
   },
}));

export const ToolPanel: React.FC = () => {
   const dispatch = useAppDispatch();
   const { filesReady } = useSignalRState();
   const { coatType, coatThickness, selectedImageView, previewZoom, selectedLayer } =
      useAppSelector(stateOfGeneralSlice);
   const [coatExpanded, setCoatExpanded] = useState(true);
   const [thicknessExpanded, setThicknessExpanded] = useState(true);
   const [zoomExpanded, setZoomExpanded] = useState(true);
   const [collapsed, setCollapsed] = useState(false);

   const zoomIn = () => {
      const next = ZOOM_LEVELS.find((z) => z > previewZoom);
      if (next) dispatch(setPreviewZoom(next));
   };
   const zoomOut = () => {
      const prev = [...ZOOM_LEVELS].reverse().find((z) => z < previewZoom);
      if (prev) dispatch(setPreviewZoom(prev));
   };
   const resetZoom = () => dispatch(setPreviewZoom(100));

   const layers = filesReady?.scodixFileData ?? [];
   const validLayerValue =
      layers.length > 0
         ? layers.some((l) => l.Layer === selectedLayer)
            ? selectedLayer
            : (layers[0]?.Layer ?? '')
         : '';

   useEffect(() => {
      if (!layers.length || selectedLayer) return;
      let layerToUse: string | null = null;
      try {
         layerToUse = sessionStorage.getItem('previewLayer');
      } catch (e) {
         // ignore
      }
      if (layerToUse && layers.some((l) => l.Layer === layerToUse)) {
         dispatch(setSelectedLayer(layerToUse));
      } else {
         const first = layers[0]?.Layer ?? '';
         if (first) {
            dispatch(setSelectedLayer(first));
            try {
               sessionStorage.setItem('previewLayer', first);
            } catch (e) {
               // ignore
            }
         }
      }
   }, [layers, selectedLayer, dispatch]);

   const handleLayerChange = useCallback(
      (newLayer: string) => {
         dispatch(setSelectedLayer(newLayer));
         try {
            sessionStorage.setItem('previewLayer', newLayer);
            window.dispatchEvent(new CustomEvent('preview-layer-changed', { detail: { layer: newLayer } }));
         } catch (e) {
            // ignore
         }
      },
      [dispatch],
   );

   const getCurrentCoatType = (): CoatType => {
      if (coatType) return coatType;
      if (selectedImageView === 'sense') return 'varnish';
      if (selectedImageView === 'silver foil') return 'silver';
      if (selectedImageView === 'Preview') return 'gold';
      return 'gold';
   };

   const currentCoat = getCurrentCoatType();

   if (collapsed) {
      return (
         <PanelAside sx={{ width: COLLAPSED_WIDTH, minWidth: COLLAPSED_WIDTH, overflowY: 'hidden' }}>
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
               }}
            >
               <Tooltip title='Expand panel' placement='right'>
                  <IconButton size='small' onClick={() => setCollapsed(false)} sx={{ color: PRIMARY_COLOR }}>
                     <MenuIcon fontSize='small' />
                  </IconButton>
               </Tooltip>
            </Box>
         </PanelAside>
      );
   }

   return (
      <PanelAside>
         <PanelHeader>
            <BrushIcon sx={{ fontSize: 20, color: PRIMARY_COLOR }} />
            <Typography
               variant='body2'
               sx={{ fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', flex: 1 }}
            >
               Tools
            </Typography>
            <Tooltip title='Collapse panel' placement='right'>
               <IconButton size='small' onClick={() => setCollapsed(true)} sx={{ color: 'text.secondary', ml: 'auto' }}>
                  <ChevronLeftIcon fontSize='small' />
               </IconButton>
            </Tooltip>
         </PanelHeader>

         {layers.length > 0 && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
               <Typography
                  variant='caption'
                  sx={{
                     fontWeight: 600,
                     letterSpacing: '0.08em',
                     color: 'text.secondary',
                     display: 'block',
                     mb: 1,
                  }}
               >
                  Selected Layer
               </Typography>
               <Select
                  fullWidth
                  value={validLayerValue}
                  onChange={(e) => handleLayerChange(e.target.value)}
                  displayEmpty
                  size='small'
                  sx={{
                     fontSize: 14,
                     '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center', gap: 1 },
                     '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                     '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${PRIMARY_COLOR}80` },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: PRIMARY_COLOR,
                        borderWidth: 1,
                     },
                  }}
                  renderValue={(value) => (
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GridViewIcon sx={{ fontSize: 18, color: PRIMARY_COLOR }} />
                        {value || 'Select layer'}
                     </Box>
                  )}
               >
                  {layers.map((layer, index) => (
                     <MenuItem key={`${layer.Layer}-${layer.Uri}-${index}`} value={layer.Layer}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <GridViewIcon sx={{ fontSize: 18, color: PRIMARY_COLOR }} />
                           {layer.Layer}
                        </Box>
                     </MenuItem>
                  ))}
               </Select>
            </Box>
         )}

         <StyledAccordion expanded={coatExpanded} onChange={() => setCoatExpanded(!coatExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: PRIMARY_COLOR }} />}>
               <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, letterSpacing: '0.08em', color: 'text.secondary' }}
               >
                  Embellishment Type
               </Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Box>
                     <Typography
                        variant='caption'
                        sx={{
                           fontWeight: 600,
                           letterSpacing: '0.06em',
                           color: 'text.secondary',
                           display: 'block',
                           mb: 0.5,
                        }}
                     >
                        Sense
                     </Typography>
                     <Box
                        sx={{
                           display: 'grid',
                           gridTemplateColumns: 'repeat(5, 1fr)',
                           gap: 0.75,
                        }}
                     >
                        {varnishPaletteOptions.map((opt) => (
                           <Tooltip key={opt.value} title={opt.label} placement='top' arrow>
                              <MetallicSwatch
                                 type='button'
                                 aria-label={opt.label}
                                 gradient={opt.gradient}
                                 isSelected={currentCoat === opt.value}
                                 onClick={() => dispatch(setCoatType(opt.value))}
                              />
                           </Tooltip>
                        ))}
                     </Box>
                  </Box>
                  <Box>
                     <Typography
                        variant='caption'
                        sx={{
                           fontWeight: 600,
                           letterSpacing: '0.06em',
                           color: 'text.secondary',
                           display: 'block',
                           mb: 0.5,
                        }}
                     >
                        Foil
                     </Typography>
                     <Box
                        sx={{
                           display: 'grid',
                           gridTemplateColumns: 'repeat(5, 1fr)',
                           gap: 0.75,
                        }}
                     >
                        {coatOptions.map((opt) => (
                           <Tooltip key={opt.value} title={opt.label} placement='top' arrow>
                              <MetallicSwatch
                                 type='button'
                                 aria-label={opt.label}
                                 gradient={opt.gradient}
                                 isSelected={currentCoat === opt.value}
                                 onClick={() => dispatch(setCoatType(opt.value))}
                              />
                           </Tooltip>
                        ))}
                     </Box>
                  </Box>
               </Box>
            </AccordionDetails>
         </StyledAccordion>

         <StyledAccordion
            expanded={thicknessExpanded}
            onChange={() => setThicknessExpanded(!thicknessExpanded)}
         >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: PRIMARY_COLOR }} />}>
               <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, letterSpacing: '0.08em', color: 'text.secondary' }}
               >
                  Effect Height
               </Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
                  {thicknessOptions.map((opt) => (
                     <Button
                        key={opt.value}
                        variant='text'
                        onClick={() => dispatch(setCoatThickness(opt.value))}
                        sx={{
                           flex: 1,
                           flexDirection: 'column',
                           gap: 0.5,
                           py: 1,
                           fontSize: 11,
                           border: '1px solid',
                           borderColor: coatThickness === opt.value ? `${PRIMARY_COLOR}4D` : 'transparent',
                           backgroundColor:
                              coatThickness === opt.value ? `${PRIMARY_COLOR}1A` : 'transparent',
                           color: coatThickness === opt.value ? 'text.primary' : 'text.secondary',
                           '&:hover': {
                              backgroundColor:
                                 coatThickness === opt.value ? `${PRIMARY_COLOR}26` : 'action.hover',
                           },
                        }}
                     >
                        <Box
                           sx={{
                              width: 24,
                              height: opt.height,
                              borderRadius: '4px',
                              backgroundColor:
                                 coatThickness === opt.value ? PRIMARY_COLOR : 'action.disabledBackground',
                           }}
                        />
                        {opt.label}
                     </Button>
                  ))}
               </Box>
               <Box sx={{ px: 0.5 }}>
                  <Typography
                     variant='caption'
                     sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: 10 }}
                  >
                     Preview
                  </Typography>
                  <Box
                     sx={{
                        mt: 0.25,
                        width: '100%',
                        height: 12,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'flex-end',
                     }}
                  >
                     <Box
                        sx={{
                           width: '100%',
                           height: coatThickness === 'min' ? '25%' : coatThickness === 'mid' ? '60%' : '100%',
                           backgroundColor: PRIMARY_COLOR,
                           borderRadius: '4px 4px 0 0',
                           transition: 'height 0.3s ease',
                        }}
                     />
                  </Box>
               </Box>
            </AccordionDetails>
         </StyledAccordion>

         <StyledAccordion expanded={zoomExpanded} onChange={() => setZoomExpanded(!zoomExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: PRIMARY_COLOR }} />}>
               <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, letterSpacing: '0.08em', color: 'text.secondary' }}
               >
                  Zoom
               </Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <IconButton
                     size='small'
                     onClick={zoomOut}
                     disabled={previewZoom <= ZOOM_MIN}
                     sx={{ color: PRIMARY_COLOR, padding: 0.5, '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                     <ZoomOutIcon sx={{ fontSize: 16, color: PRIMARY_COLOR }} />
                  </IconButton>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Typography variant='caption' sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
                        {previewZoom}%
                     </Typography>
                  </Box>
                  <IconButton
                     size='small'
                     onClick={zoomIn}
                     disabled={previewZoom >= ZOOM_MAX}
                     sx={{ color: PRIMARY_COLOR, padding: 0.5, '&.Mui-disabled': { opacity: 0.3 } }}
                  >
                     <ZoomInIcon sx={{ fontSize: 16, color: PRIMARY_COLOR }} />
                  </IconButton>
                  <IconButton
                     size='small'
                     onClick={resetZoom}
                     title='Reset to 100%'
                     sx={{ color: PRIMARY_COLOR, padding: 0.5 }}
                  >
                     <FullscreenIcon sx={{ fontSize: 16, color: PRIMARY_COLOR }} />
                  </IconButton>
               </Box>
               <Slider
                  value={previewZoom}
                  onChange={(_, value) => dispatch(setPreviewZoom(value as number))}
                  min={ZOOM_MIN}
                  max={ZOOM_MAX}
                  step={1}
                  size='small'
                  sx={{
                     color: PRIMARY_COLOR,
                     '& .MuiSlider-thumb': { width: 12, height: 12 },
                     '& .MuiSlider-rail': { opacity: 0.5 },
                  }}
               />
               <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, mt: 0.5 }}>
                  {[25, 50, 100, 200, 300, 400].map((z) => (
                     <Button
                        key={z}
                        size='small'
                        onClick={() => dispatch(setPreviewZoom(z))}
                        sx={{
                           flex: 1,
                           minWidth: 0,
                           px: 0.5,
                           py: 0.25,
                           fontSize: 10,
                           fontFamily: 'monospace',
                           border: '1px solid',
                           borderColor: previewZoom === z ? `${PRIMARY_COLOR}4D` : 'transparent',
                           backgroundColor: previewZoom === z ? `${PRIMARY_COLOR}1A` : 'transparent',
                           color: previewZoom === z ? 'text.primary' : 'text.secondary',
                           '&:hover': {
                              backgroundColor: previewZoom === z ? `${PRIMARY_COLOR}26` : 'action.hover',
                           },
                        }}
                     >
                        {z}%
                     </Button>
                  ))}
               </Box>
            </AccordionDetails>
         </StyledAccordion>

         <Box
            sx={{
               mt: 'auto',
               p: 1.5,
               borderTop: 1,
               borderColor: 'divider',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  fontFamily: 'monospace',
                  fontSize: 10,
                  color: 'text.secondary',
               }}
            >
               <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Embellishment</span>
                  <span style={{ fontWeight: 500, color: 'inherit' }}>
                     {coatOptions.find((c) => c.value === currentCoat)?.label ?? currentCoat}
                  </span>
               </Box>
               <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Effect Height</span>
                  <span style={{ fontWeight: 500, color: 'inherit' }}>{coatThickness.toUpperCase()}</span>
               </Box>
               <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Zoom</span>
                  <span style={{ fontWeight: 500, color: 'inherit' }}>{previewZoom}%</span>
               </Box>
               {validLayerValue && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                     <span>Layer</span>
                     <span style={{ fontWeight: 500, color: 'inherit' }}>{validLayerValue}</span>
                  </Box>
               )}
            </Box>
         </Box>
      </PanelAside>
   );
};
